import { DEFAULT_TIMEZONE } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, Field, Icon, Input, RadioGroup, Select, Switch, toast } from "@sagot/ui";
import { useState } from "react";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceDetails } from "../../../session/workspace-context";
import type { AgentSummary, EvalSchedule } from "../shared/types";
import { CsvField, csvShape } from "./csv-field";
import { DEFAULT_GENERATE, GenerateOptions, toGenerateIn, type GenerateValue } from "./generate-options";
import { useEvalMutations } from "./use-evals";

const ALL_AGENTS = "";
const NAME_MAX = 120;

export function ScheduleField({ value, onChange, disabled }: { value: EvalSchedule; onChange: (v: EvalSchedule) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const timezone = useWorkspaceDetails().data?.timezone ?? DEFAULT_TIMEZONE;
  return (
    <fieldset disabled={disabled}>
      <legend className="label">{t("build.evaluations.newSet.schedule")}</legend>
      <RadioGroup
        aria-label={t("build.evaluations.newSet.schedule")}
        variant="cards"
        className="grid grid-cols-1 gap-2 sm:grid-cols-3"
        value={value}
        onValueChange={onChange}
        options={[
          { value: "manual", label: t("build.evaluations.newSet.manual"), description: t("build.evaluations.newSet.manualBody") },
          { value: "nightly", label: t("build.evaluations.newSet.nightly"), description: t("build.evaluations.newSet.nightlyBody", { timezone }) },
          { value: "on_knowledge_change", label: t("build.evaluations.newSet.onChange"), description: t("build.evaluations.newSet.onChangeBody") },
        ]}
      />
    </fieldset>
  );
}

interface NewSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  agents: readonly AgentSummary[];
  canRun: boolean;
}

/** `new-set-modal`: create set → import CSV or generate with AI → queue the first run. */
export function NewSetDialog({ open, onOpenChange, agentId, agents, canRun }: NewSetDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const m = useEvalMutations(agentId);
  const [name, setName] = useState("");
  const [agent, setAgent] = useState(agentId);
  const [ai, setAi] = useState(false);
  const [csv, setCsv] = useState("");
  const [generate, setGenerate] = useState<GenerateValue>(DEFAULT_GENERATE);
  const [schedule, setSchedule] = useState<EvalSchedule>("manual");
  const [alert, setAlert] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = (next: boolean) => {
    if (!next) { setName(""); setCsv(""); setAi(false); setError(null); }
    onOpenChange(next);
  };
  const submit = async () => {
    if (!name.trim()) { setError(t("build.evaluations.newSet.nameRequired")); return; }
    if (!ai && csvShape(csv).rows === 0) { setError(t("build.evaluations.newSet.csvRequired")); return; }
    setBusy(true);
    setError(null);
    try {
      const set = await m.createSet.mutateAsync({ name: name.trim(), agent_id: agent || null, origin: ai ? "ai_generated" : "csv", schedule, alert_on_drop: alert });
      if (ai) await m.generate.mutateAsync({ id: set.id, body: toGenerateIn(generate) });
      else await m.importCsv.mutateAsync({ id: set.id, csv, dryRun: false });
      if (canRun) await m.run.mutateAsync(set.id);
      toast.success(t(canRun ? "build.evaluations.newSet.created" : "build.evaluations.newSet.createdNoRun"));
      close(false);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        title={t("build.evaluations.newSet.title")}
        closeLabel={t("common.close")}
        className="max-w-2xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => { close(false); }}>{t("common.cancel")}</Button>
            <Button loading={busy} onClick={() => void submit()}><Icon name="check" />{t("build.evaluations.newSet.submit")}</Button>
          </>
        }
      >
        <div className="max-h-[60vh] space-y-5 overflow-y-auto">
          {error ? <FormAlert title={error} /> : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t("build.evaluations.newSet.name")}>
              <Input value={name} maxLength={NAME_MAX} placeholder={t("build.evaluations.newSet.namePlaceholder")} onChange={(e) => { setName(e.target.value); }} />
            </Field>
            <Field label={t("build.evaluations.newSet.agent")}>
              <Select value={agent} onChange={(e) => { setAgent(e.target.value); }}>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                <option value={ALL_AGENTS}>{t("build.evaluations.sets.allAgents")}</option>
              </Select>
            </Field>
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-line p-4">
            <div>
              <label htmlFor="eval-ai" className="flex items-center gap-2 text-sm font-bold"><Icon name="sparkles" className="size-4 text-primary-ink" />{t("build.evaluations.newSet.aiSwitch")}</label>
              <p className="mt-0.5 text-xs text-fg-muted">{t("build.evaluations.generate.body")}</p>
            </div>
            <Switch id="eval-ai" className="mt-1" checked={ai} onCheckedChange={setAi} />
          </div>
          {ai ? <GenerateOptions value={generate} onChange={setGenerate} /> : <CsvField value={csv} onChange={setCsv} />}
          <ScheduleField value={schedule} onChange={setSchedule} />
          <Checkbox checked={alert} onCheckedChange={(v) => { setAlert(v === true); }} label={t("build.evaluations.setSheet.alert")} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
